import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Meteor } from 'meteor/meteor';
import { find, get, includes, isEmpty, isEqual, keys, last } from 'lodash';
import shp from 'shpjs';
import uuid from 'uuid/v1';
import * as topojson from 'topojson-client';
import { Bert } from 'meteor/themeteorchef:bert';
import turf from '@turf/turf';

import * as atlasOps from '../../../api/Atlas/atlas-operations';
import * as layerOps from '../../../api/Layers/layer-operations';
import { styleFetchOne } from '../../../api/Styles/style-methods-client';

import { readUploadedFileAsText } from '../../../modules/export-import-files';
import { featurePoint2LatLng, featureGetZoom } from '../../../modules/geojson-utilities';
import getDefaultStyle from '../../../modules/get-default-style';

import LeafletMap from '../../components/LeafletMap';

const { defaultMapCenter } = Meteor.settings.public;

let addOnlineBaseMap;
let drawCustomButtons;
let drawAIInterestArea;
let drawSingleLayer;

class MapContainerFocusedTrainAtlas extends Component {
  aoisLayer = null;

  entitiesLayer = null;

  focusedTrainAtlasMap = null;

  focusedTrainAtlasMapComponent = React.createRef();

  orthoLayer = null;

  randomMapId = uuid();

  state = {
    clientModulesReady: false,
    isLeafletMapLoaded: get(this, 'focusedTrainAtlasMapComponent.current.state.isMapLoaded', false),
    isAIAtlasMapLoaded: false
  };

  componentDidMount() {
    const { clientModulesReady } = this.state;
    if (!clientModulesReady) this.loadClientModules();
  }

  async componentDidUpdate(prevProps) {
    const { aois, atlas, entities, layers, selectedLayer } = this.props;
    const { clientModulesReady, isLeafletMapLoaded, isAIAtlasMapLoaded } = this.state;
    const {
      aois: prevAOIS,
      atlas: prevAtlas,
      entities: prevEntities,
      selectedLayer: prevSelectedLayer
    } = prevProps;

    if (clientModulesReady && isLeafletMapLoaded && !isAIAtlasMapLoaded) this.initializeMap();

    if (isAIAtlasMapLoaded) {
      if (!isEqual(atlas, prevAtlas))
        drawCustomButtons({
          map: {
            selectedButtons: {
              Attribution: true,
              Home: true,
              QRCode: false,
              Scale: true,
              Search: false,
              Title: true,
              Zoom: true
            },
            mapLocation: defaultMapCenter,
            name: atlasOps.getName(atlas),
            publicId: atlasOps.getId(atlas),
            shared: atlasOps.isShared(atlas)
          },
          type: 'atlas',
          leafletMap: this.focusedTrainAtlasMap,
          fileInputButton: true,
          loadFileFunction: this.loadFileMap
        });

      if (!isEqual(selectedLayer, prevSelectedLayer) && !isEmpty(selectedLayer)) {
        const wmsLayer = find(layers, ['_id', selectedLayer]);
        const style =
          (await styleFetchOne(layerOps.getStyleId(wmsLayer))) || getDefaultStyle(wmsLayer);
        this.orthoLayer = await drawSingleLayer({
          layer: wmsLayer,
          leafletMap: this.focusedTrainAtlasMap,
          layerToDraw: this.orthoLayer,
          style,
          isUpdate: true
        });
      }

      if (!isEqual(aois, prevAOIS) || !isEqual(selectedLayer, prevSelectedLayer))
        this.aoisLayer = drawAIInterestArea({
          aiInterestArea: aois,
          aiInterestAreaLayer: this.aoisLayer,
          isEntities: false,
          isUpdate: true,
          leafletMap: this.focusedTrainAtlasMap
        });

      if (!isEqual(entities, prevEntities) || !isEqual(selectedLayer, prevSelectedLayer))
        this.entitiesLayer = drawAIInterestArea({
          aiInterestArea: entities,
          aiInterestAreaLayer: this.entitiesLayer,
          isEntities: true,
          isUpdate: true,
          leafletMap: this.focusedTrainAtlasMap
        });
    }
  }

  updateMapState = newState => this.setState({ isLeafletMapLoaded: newState });

  initializeMap = () => {
    const { aois, atlas, entities, isEntities } = this.props;

    this.focusedTrainAtlasMap = get(this, 'focusedTrainAtlasMapComponent.current.leafletMap');
    this.focusedTrainAtlasMap.setView(
      featurePoint2LatLng(defaultMapCenter),
      featureGetZoom(defaultMapCenter)
    );

    drawCustomButtons({
      map: {
        selectedButtons: {
          Attribution: true,
          Home: true,
          QRCode: false,
          Scale: true,
          Search: false,
          Title: true,
          Zoom: true
        },
        mapLocation: defaultMapCenter,
        name: atlasOps.getName(atlas),
        publicId: atlasOps.getId(atlas),
        shared: atlasOps.isShared(atlas)
      },
      leafletMap: this.focusedTrainAtlasMap,
      fileInputButton: true,
      loadFileFunction: this.loadFileMap
    });

    addOnlineBaseMap(this.focusedTrainAtlasMap);

    this.aoisLayer = drawAIInterestArea({
      aiInterestArea: aois,
      aiInterestAreaLayer: this.aoisLayer,
      isEntities,
      leafletMap: this.focusedTrainAtlasMap
    });

    this.entitiesLayer = drawAIInterestArea({
      aiInterestArea: entities,
      aiInterestAreaLayer: this.entitiesLayer,
      isEntities,
      leafletMap: this.focusedTrainAtlasMap
    });

    this.setState({ isAIAtlasMapLoaded: true });
  };

  loadFileMap = async event => {
    const { setAIInterestArea } = this.props;

    const targetFile = event.target.files[0];
    const extension = last(targetFile.name.split('.'));

    try {
      const stringResult = await readUploadedFileAsText(targetFile);

      let geojson = {};
      let key = '';
      let arrayBuffer = {};
      switch (extension) {
        case 'geojson':
          geojson = JSON.parse(stringResult);
          break;
        case 'topoJSON':
          geojson = JSON.parse(stringResult);
          [key] = keys(geojson.objects);
          geojson = topojson.feature(geojson, geojson.objects[key]);
          break;
        case 'zip':
          arrayBuffer = await targetFile.arrayBuffer();
          geojson = await shp(arrayBuffer);
          break;
        default:
          break;
      }

      let isValidType = true;
      if (turf.getType(geojson) === 'FeatureCollection')
        turf.featureEach(geojson, currentFeature => {
          if (!includes(['Polygon', 'MultiPolygon'], turf.getType(currentFeature)))
            isValidType = false;
        });
      else if (!includes(['Polygon', 'MultiPolygon'], turf.getType(geojson))) isValidType = false;

      if (!isValidType) Bert.alert('Geometry should be an area');
      else setAIInterestArea(geojson);
    } catch (error) {
      setAIInterestArea({});
      Bert.alert('Geometry File is not as expected');
    }
  };

  async loadClientModules() {
    if (Meteor.isClient) {
      ({ addOnlineBaseMap, drawCustomButtons, drawAIInterestArea, drawSingleLayer } = await import(
        '../../../modules/leaflet-operations'
      ));
      this.setState({ clientModulesReady: true });
    }
  }

  render() {
    const { deployEnv } = this.props;
    return (
      <LeafletMap
        deployEnv={deployEnv}
        isAtlas
        isRounded
        mapId={this.randomMapId}
        ref={this.focusedTrainAtlasMapComponent}
        updateMapState={this.updateMapState}
      />
    );
  }
}

MapContainerFocusedTrainAtlas.propTypes = {
  aois: PropTypes.object.isRequired,
  atlas: PropTypes.object.isRequired,
  deployEnv: PropTypes.string.isRequired,
  entities: PropTypes.object.isRequired,
  isEntities: PropTypes.bool.isRequired,
  layers: PropTypes.array.isRequired,
  setAIInterestArea: PropTypes.func.isRequired,
  selectedLayer: PropTypes.string.isRequired
};

export default MapContainerFocusedTrainAtlas;
